import React, { useState, useMemo } from "react";
import { useModel } from "../../../../context/ModelContext";
import { Model } from "../../../../types";
import { Search, Check, Sparkles, Cpu, X, RotateCcw, Info } from "lucide-react";

interface ModelSelectorProps {
  selectedModelId?: string;
  onModelSelected: (modelId: string) => void;
  onModelCleared: () => void;
  isEnabled?: boolean;
  requireToolSupport?: boolean;
  /** Model chosen automatically for this VA — badged so the user can see (and override) the AI's pick. */
  recommendedModelId?: string;
  /** One-line plain-language justification shown next to the recommended model. */
  recommendationReason?: string;
}

/** Shown on a model the connector rule kept out of the AI recommendation. */
const NO_CONNECTOR_SUPPORT_REASON =
  "This model is not recommended because it does not support connectors.";

const ModelSelector: React.FC<ModelSelectorProps> = ({
  selectedModelId,
  onModelSelected,
  onModelCleared,
  isEnabled = true,
  requireToolSupport = false,
  recommendedModelId,
  recommendationReason,
}) => {
  const { models } = useModel();
  const [searchQuery, setSearchQuery] = useState("");

  const hasSelectedModel = selectedModelId && selectedModelId.trim() !== "";

  const enabledModels = useMemo(() => {
  let filtered = models.filter(model => model.is_enabled);
  
  if (requireToolSupport) {
    filtered = filtered.filter(model => model.tool_call_support === true);
  }
  
  return filtered;
}, [models, requireToolSupport]); 

    const filteredModels = useMemo(() => {
    let filtered = enabledModels;
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(model =>
        model.display_name.toLowerCase().includes(query) ||
        model.provider.toLowerCase().includes(query) ||
        model.name.toLowerCase().includes(query)
      );
    }
    
    // Sort: Selected first, then the AI recommendation, then alphabetical
    filtered = [...filtered].sort((a, b) => {
      // Selected model always first
      if (hasSelectedModel) {
        if (a.foundation_model_id === selectedModelId) return -1;
        if (b.foundation_model_id === selectedModelId) return 1;
      }
      // Keep the recommendation visible without scrolling when the user has
      // overridden it with a different model.
      if (recommendedModelId) {
        if (a.foundation_model_id === recommendedModelId) return -1;
        if (b.foundation_model_id === recommendedModelId) return 1;
      }
      // Then alphabetical order
      return a.display_name.localeCompare(b.display_name);
    });

    return filtered;
  }, [enabledModels, searchQuery, selectedModelId, hasSelectedModel, recommendedModelId]);

    const handleModelSelect = (modelId: string) => {
    if (selectedModelId === modelId) {
      return;
    }
    onModelSelected(modelId);
  };

  const handleClearSelection = () => {
    onModelCleared();
  };

  const getProviderColor = (provider: string) => {
    const colors: { [key: string]: string } = {
      'anthropic': 'bg-orange-100 text-orange-700',
      'openai': 'bg-green-100 text-green-700',
      'meta': 'bg-blue-100 text-blue-700',
      'amazon': 'bg-yellow-100 text-yellow-700',
      'mistral': 'bg-purple-100 text-purple-700',
      'cohere': 'bg-pink-100 text-pink-700',
    };
    return colors[provider.toLowerCase()] || 'bg-gray-100 text-gray-700';
  };

  if (!isEnabled) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Cpu className="h-8 w-8 mx-auto mb-2 text-gray-400" />
        <p className="text-sm">Model selection is disabled</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search models..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 bg-white text-gray-900 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
        />
      </div>

      {/* Models List - All models treated equally */}
      <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
        {filteredModels.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
           <Cpu className="h-8 w-8 mx-auto mb-2 text-gray-400" />
              <p className="text-sm">
               {requireToolSupport 
                 ? "No models with tool support available" 
                   : "No models found"}
                </p>
              </div>
           ) : (
          filteredModels.map((model, index) => {
            const isSelected = hasSelectedModel && selectedModelId === model.foundation_model_id;
            const isDefault = model.default;
            const isFirstAndSelected = index === 0 && isSelected;
            const isRecommended = !!recommendedModelId && recommendedModelId === model.foundation_model_id;
            // Only an explicit `false` counts — an older model row that predates
            // the flag arrives undefined and must not be labelled unsupported.
            const lacksConnectorSupport = model.connector_support === false;

            return (
              <div key={model.foundation_model_id}>
                {/* Show badge for first item when it's selected */}
                {isFirstAndSelected && (
                  <div className="mb-2 text-xs text-violet-600 font-medium flex items-center gap-1">
                    <Check className="h-3 w-3" />
                    Currently Selected Model
                  </div>
                )}
                
                {/* Model Card - Same style for all models including default */}
                <div
                  onClick={() => handleModelSelect(model.foundation_model_id)}
                  className={`relative p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    isSelected
                      ? 'border-violet-500 bg-violet-50'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      {/* Radio Button - SAME FOR ALL MODELS */}
                      <div className="flex items-center h-5 pt-0.5">
                        <div
                          className={`h-4 w-4 rounded-full border-2 flex items-center justify-center ${
                            isSelected
                              ? 'border-violet-500 bg-violet-500'
                              : 'border-gray-300 bg-white'
                          }`}
                        >
                          {isSelected && (
                            <div className="h-2 w-2 rounded-full bg-white" />
                          )}
                        </div>
                      </div>

                      {/* Model Info */}
                      <div className="flex-1 min-w-0">
                        {/* Model Name and Badges */}
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-medium text-gray-900">
                            {model.display_name}
                          </span>
                          {/* Show Default badge */}
                          {isDefault && (
                            <span className="inline-flex items-center text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                              <Sparkles className="h-3 w-3 mr-1" />
                              Default
                            </span>
                          )}
                          {/* Provider badge */}
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getProviderColor(model.provider)}`}>
                            {model.provider}
                          </span>
                          {/* AI recommendation badge */}
                          {isRecommended && (
                            <span className="inline-flex items-center text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">
                              <Sparkles className="h-3 w-3 mr-1" />
                              AI Recommended
                            </span>
                          )}
                        </div>

                        {/* Why the AI picked this model */}
                        {isRecommended && recommendationReason && (
                          <p className="text-xs text-amber-700 mt-1">
                            {recommendationReason}
                          </p>
                        )}

                        {/* Why the AI passed over this model */}
                        {lacksConnectorSupport && (
                          <p className="text-xs text-gray-500 mt-1 flex items-start gap-1">
                            <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
                            {NO_CONNECTOR_SUPPORT_REASON}
                          </p>
                        )}

                        {/* Short Description */}
                        {model.short_description && (
                          <p className="text-xs text-gray-500 mt-1 line-clamp-1">
                            {model.short_description}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Selected Checkmark and Remove Button */}
                    {isSelected && (
                      <div className="flex items-center gap-2 ml-2">
                        <Check className="h-5 w-5 text-violet-600 flex-shrink-0" />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleClearSelection();
                          }}
                          className="p-1 hover:bg-violet-100 rounded-full transition-colors"
                          title="Clear selection"
                        >
                          <X className="h-4 w-4 text-violet-600" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ModelSelector;